// src/UpdatedStats.jsx
import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

export default function Performance() {
  const [rows, setRows] = useState([]); // [{ Name, onTime, safety, trainingStatus }]
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  const toNum = (val) => {
    if (val == null) return 0;
    if (typeof val === 'number') return Number.isFinite(val) ? val : 0;
    const n = Number(String(val).replace('%', '').trim());
    return Number.isFinite(n) ? n : 0;
  };

  const normStatus = (s) => String(s || '').trim().toUpperCase().replace(/\s+/g, '_');
  const prettyStatus = (s) => {
    const n = normStatus(s);
    if (!n) return '—';
    if (n === 'NOT_STARTED') return 'Not Started';
    if (n === 'IN_PROGRESS') return 'In Progress';
    if (n === 'COMPLETE') return 'Complete';
    // fallback: title-case
    return n.toLowerCase().replace(/(^|_)./g, (m) => m.slice(-1).toUpperCase()).replace(/_/g, ' ');
  };
  const statusStyle = (s) => {
    const n = normStatus(s);
    let bg = '#444', color = '#fff', border = '#333';
    if (n === 'NOT_STARTED') { bg = '#3b0d12'; border = '#5a1a21'; }
    else if (n === 'IN_PROGRESS') { bg = '#4a3a04'; border = '#6b5706'; }
    else if (n === 'COMPLETE') { bg = '#0f3b1a'; border = '#1f6a35'; }
    return {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 700,
      background: bg,
      color,
      border: `1px solid ${border}`,
      minWidth: 88,
      textAlign: 'center',
    };
  };

  async function authHeaders() {
    const { tokens } = await fetchAuthSession();
    const jwt =
      tokens?.idToken?.toString?.() ??
      tokens?.accessToken?.toString?.();
    return jwt ? { Authorization: `Bearer ${jwt}` } : {};
  }

  useEffect(() => {
    const ctrl = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setErrorMsg('');

        const resp = await fetch(
          'https://2af2e5kjj9.execute-api.us-east-1.amazonaws.com/prod/performance',
          {
            headers: {
              Accept: 'application/json',
              ...(await authHeaders()),
            },
            signal: ctrl.signal,
          }
        );

        const text = await resp.text();
        let data;
        try { data = text ? JSON.parse(text) : null; } catch { data = null; }

        if (!resp.ok) {
          if (resp.status === 401 || resp.status === 403) {
            setErrorMsg('Unauthorized. Please sign in to view performance.');
            return;
          }
          setErrorMsg(data?.message || `Failed to fetch performance data (HTTP ${resp.status}).`);
          return;
        }
        if (!Array.isArray(data)) {
          setErrorMsg('Unexpected response format.');
          return;
        }

        const normalized = data
          .map(r => ({
            Name: r?.Name || 'Unknown',
            onTime: toNum(r?.onTime),
            safety: toNum(r?.safety),
            trainingStatus: r?.trainingStatus ?? '', // may be missing on some rows
          }))
          .sort((a, b) => a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' }));

        setRows(normalized);
      } catch (e) {
        if (e.name === 'AbortError') return;
        // eslint-disable-next-line no-console
        console.error(e);
        setErrorMsg('Error fetching performance data');
      } finally {
        setLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, []);

  const nameColStyle = {
    width: '25%',
    maxWidth: '30ch',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <div>
      <section id="Title" className="formTitle colored-bg">
        <div className="formHead">
          <h3>Driver Performance Scores</h3>
        </div>
      </section>

      <section id="main" className="colored-bg">
        {loading && (
          <div className="container listDiv">
            <p>Loading performance…</p>
          </div>
        )}

        {errorMsg && !loading && (
          <div className="container listDiv">
            <p style={{ color: '#C80036' }}>{errorMsg}</p>
          </div>
        )}

        {!loading && !errorMsg && (
          <div className="container listDiv">
            <h3>On Time, Safety & Training</h3>
            <div className="scrollable-table">
              <table>
                <thead>
                  <tr>
                    <th style={nameColStyle}>Driver</th>
                    <th>On Time %</th>
                    <th>Safety</th>
                    <th>Training</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const onTimeBad = r.onTime < 95;
                    const safetyBad = r.safety < 850;
                    return (
                      <tr key={`${r.Name}-${i}`}>
                        <td style={nameColStyle}>{r.Name}</td>
                        <td style={{ color: onTimeBad ? '#C80036' : 'white' }}>
                          {Number.isFinite(r.onTime) ? r.onTime.toFixed(1) : r.onTime}
                        </td>
                        <td style={{ color: safetyBad ? '#C80036' : 'white' }}>
                          {Number.isFinite(r.safety) ? r.safety.toFixed(0) : r.safety}
                        </td>
                        <td>
                          <span style={statusStyle(r.trainingStatus)}>
                            {prettyStatus(r.trainingStatus)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="container listDiv">
              <h3>~ NOTES ~</h3>
              <ul style={{ listStyle: 'none', fontWeight: 'bolder', margin: '5px 0' }}>
                <li>
                  On Time Percentage
                  <ul style={{ textAlign: 'left', fontWeight: 400 }}>
                    <li>If your percentage is below 95% you may not be eligible for overtime or bonuses.</li>
                  </ul>
                </li>
                <li>
                  Safety Scores
                  <ul style={{ textAlign: 'left', fontWeight: 400 }}>
                    <li>If your score is below 850 you will lose your bonus hours and may not be eligible for overtime.</li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
